import { useState, useCallback, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Target,
  TrendingUp,
  BarChart3,
  Crosshair,
  Zap,
  BookOpen,
  Swords,
  ArrowRight,
  Check,
  X,
  Minus,
  FileText,
} from "lucide-react";

const TOTAL_PAGES = 8;

const PAGE_TITLES = [
  "Cover",
  "Executive Summary",
  "Competitive Landscape",
  "Feature Matrix",
  "Positioning Map",
  "White Space & Kano Analysis",
  "Action Plan & Sales Battlecard",
  "Sources",
];

const PAGE_ICONS = [
  FileText,
  Target,
  Swords,
  BarChart3,
  Crosshair,
  TrendingUp,
  Zap,
  BookOpen,
];

function Cite({ n }: { n: number | number[] }) {
  const nums = Array.isArray(n) ? n : [n];
  return (
    <sup className="text-[9px] text-blue-500 dark:text-blue-400 font-medium ml-0.5">
      [{nums.join(",")}]
    </sup>
  );
}

function FeatureIcon({ value }: { value: string }) {
  if (value === "YES") return <Check className="w-4 h-4 text-green-600" />;
  if (value === "No") return <X className="w-4 h-4 text-red-400" />;
  if (value === "Partial" || value === "Planned" || value === "Via Eatzy")
    return <Minus className="w-4 h-4 text-yellow-500" />;
  return <span className="text-xs text-muted-foreground">{value}</span>;
}

function PageContainer({ children }: { children: ReactNode }) {
  return (
    <div className="w-[816px] min-h-[1056px] bg-white dark:bg-card border border-border shadow-sm mx-auto p-12 overflow-hidden print:shadow-none print:border-none">
      {children}
    </div>
  );
}

function CoverPage() {
  return (
    <PageContainer>
      <div className="flex flex-col items-center justify-center h-full min-h-[960px] bg-gradient-to-br from-blue-700 via-blue-800 to-blue-900 rounded-lg p-12 text-white text-center -m-12">
        <p className="text-sm tracking-widest uppercase opacity-70 mb-6">
          Eatzy Social Ordering Engine
        </p>
        <h1 className="text-4xl font-bold mb-2">Competitive Analysis</h1>
        <h1 className="text-4xl font-bold mb-6">Report</h1>
        <p className="text-base opacity-80 max-w-md">
          Market Positioning, Feature Matrix & Strategic Recommendations
        </p>
        <div className="w-24 h-px bg-white/40 my-8" />
        <p className="text-sm opacity-60">Confidential</p>
        <p className="text-sm opacity-60">March 2026</p>
        <p className="text-xs opacity-40 mt-8">
          Prepared for the Eatzy Leadership Team
        </p>
      </div>
    </PageContainer>
  );
}

function ExecutiveSummaryPage() {
  return (
    <PageContainer>
      <h2 className="text-2xl font-bold text-blue-700 dark:text-blue-400 mb-6">
        Executive Summary
      </h2>

      <div className="p-5 bg-blue-50/60 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md mb-6">
        <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-2">
          Positioning Statement (Dunford Framework)
        </h3>
        <p className="text-sm italic text-foreground/80 leading-relaxed">
          For independent restaurant owners who post food content on social
          media but lose orders to DoorDash<Cite n={12} />, Eatzy Social Ordering Engine is a
          social commerce tool that turns any Instagram or Facebook food post
          into a direct order in seconds. Unlike Owner.com<Cite n={[1,3]} />, ChowNow<Cite n={[4,5]} />,
          GloriaFood<Cite n={7} />, and Menufy<Cite n={9} /> — which provide ordering systems but ignore the
          social-to-order gap — Eatzy is the only platform that connects social
          media content directly to menu items with one-tap ordering, zero
          commissions, and same-day analytics.
        </p>
      </div>

      <div className="mb-6">
        <h3 className="text-base font-semibold text-foreground mb-3">
          Top 3 Strategic Recommendations
        </h3>
        <div className="space-y-4">
          {[
            {
              num: 1,
              title: "Lead with the daily specials workflow in every demo and ad",
              detail: "No competitor solves the \"post a special, sell it instantly\" use case. This is your fastest conversion hook.",
              cite: [13, 14],
            },
            {
              num: 2,
              title: "Position against DoorDash (the problem), not Owner.com (a competitor)",
              detail: "Your ICP doesn't know Owner.com exists. They DO know they pay DoorDash $2,000+/month in commissions. Frame Eatzy as the tool that redirects DoorDash revenue back to them.",
              cite: [12, 15],
            },
            {
              num: 3,
              title: "Own 'Social Food Commerce' as a new category before anyone else claims it",
              detail: "No competitor currently markets the social-to-order conversion explicitly. Define and own this category.",
              cite: [13, 16],
            },
          ].map((rec) => (
            <div key={rec.num} className="flex gap-3 items-start">
              <div className="w-7 h-7 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold shrink-0 mt-0.5">
                {rec.num}
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {rec.title}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {rec.detail}<Cite n={rec.cite} />
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-foreground mb-2">
          Market Context
        </h3>
        <ul className="text-sm text-foreground/80 space-y-1.5">
          {[
            { text: "Total addressable market: ~660,000 US restaurants", cite: 11 },
            { text: "Primary ICP: Independent, owner-operated, 1-2 locations, $25K-$150K/mo revenue", cite: 13 },
            { text: "Commission leakage to 3PDs: 15-30% per order ($7,500+/month for mid-volume)", cite: [12, 19] },
            { text: "No existing platform connects social content directly to menu-item ordering", cite: [1, 4, 7, 9] },
            { text: "Category being created: \"Social Food Commerce\"", cite: [13, 16] },
          ].map((item, i) => (
            <li key={i} className="flex gap-2 items-start">
              <ArrowRight className="w-3.5 h-3.5 mt-0.5 text-blue-500 shrink-0" />
              <span>{item.text}<Cite n={item.cite} /></span>
            </li>
          ))}
        </ul>
      </div>
    </PageContainer>
  );
}

function CompetitiveLandscapePage() {
  const directCompetitors = [
    {
      name: "Owner.com",
      pricing: "$499/mo + 5% customer fee",
      stage: "Series C, $120M, $1B valuation",
      strength: "Full-stack platform: website, app, ordering, marketing, delivery",
      weakness: "High price excludes small operators; no social-to-order workflow",
      cite: [1, 2, 3],
    },
    {
      name: "ChowNow",
      pricing: "$119-$298/mo, no commissions",
      stage: "Series C, $63M (no new funding since 2019)",
      strength: "#1 rated support (G2); strong commission-free positioning",
      weakness: "Slow feature updates; basic analytics; no social integration",
      cite: [4, 5, 6],
    },
    {
      name: "GloriaFood",
      pricing: "Free core, $0-$59/mo premium",
      stage: "Acquired by Oracle (2020)",
      strength: "Free unlimited orders; very low barrier to entry",
      weakness: "Declining support (2025); zero social distribution; no innovation",
      cite: [7, 8],
    },
    {
      name: "Menufy",
      pricing: "$1.75/order flat, no monthly fee",
      stage: "Owned by HungerRush",
      strength: "Low per-order cost; branded websites + apps",
      weakness: "Per-order fee penalizes growth; no social commerce",
      cite: [9, 10],
    },
  ];

  const indirectCompetitors = [
    {
      name: "DoorDash",
      model: "15-30% commission per order",
      role: "Largest 3PD; captures orders generated by restaurant social media",
      threat: "Restaurants create demand via IG/FB, but customers default to DoorDash to order",
      cite: 12,
    },
    {
      name: "Uber Eats",
      model: "15-30% commission per order",
      role: "Second-largest 3PD; same demand interception pattern",
      threat: "Customer sees food on IG, opens Uber Eats, restaurant pays 25%+ commission",
      cite: 12,
    },
    {
      name: "Grubhub",
      model: "5-30% commission per order",
      role: "Third 3PD marketplace; additional demand interception channel",
      threat: "Same commission extraction model; 5-20% marketing fee + 10% delivery fee + processing",
      cite: 19,
    },
    {
      name: "Instagram / Meta",
      model: "Free (ad-funded)",
      role: "Primary content platform. Long-term threat if they add native food ordering.",
      threat: "Meta has never built deep restaurant commerce. Complex POS/delivery integration gives Eatzy a long runway.",
      cite: 16,
    },
  ];

  return (
    <PageContainer>
      <h2 className="text-2xl font-bold text-blue-700 dark:text-blue-400 mb-5">
        Competitive Landscape
      </h2>

      <div className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">
          Direct Competitors
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-[10px] border-collapse border border-border">
            <thead>
              <tr className="bg-blue-700 text-white">
                <th className="p-1.5 text-left font-medium">Company</th>
                <th className="p-1.5 text-left font-medium">Pricing</th>
                <th className="p-1.5 text-left font-medium">Stage</th>
                <th className="p-1.5 text-left font-medium">Top Strength</th>
                <th className="p-1.5 text-left font-medium">Top Weakness</th>
              </tr>
            </thead>
            <tbody>
              {directCompetitors.map((c, i) => (
                <tr
                  key={c.name}
                  className={
                    i % 2 === 0
                      ? "bg-blue-50/40 dark:bg-blue-950/20"
                      : "bg-white dark:bg-background"
                  }
                >
                  <td className="p-1.5 font-semibold text-foreground">{c.name}<Cite n={c.cite} /></td>
                  <td className="p-1.5 text-foreground/80">{c.pricing}</td>
                  <td className="p-1.5 text-foreground/80">{c.stage}</td>
                  <td className="p-1.5 text-foreground/80">{c.strength}</td>
                  <td className="p-1.5 text-red-600 dark:text-red-400">{c.weakness}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-foreground mb-3">
          Indirect Competitors (Demand Interceptors)
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-[10px] border-collapse border border-border">
            <thead>
              <tr className="bg-blue-700 text-white">
                <th className="p-1.5 text-left font-medium">Company</th>
                <th className="p-1.5 text-left font-medium">Model</th>
                <th className="p-1.5 text-left font-medium">Role in Ecosystem</th>
                <th className="p-1.5 text-left font-medium">Why They're Competition</th>
              </tr>
            </thead>
            <tbody>
              {indirectCompetitors.map((c, i) => (
                <tr
                  key={c.name}
                  className={
                    i % 2 === 0
                      ? "bg-blue-50/40 dark:bg-blue-950/20"
                      : "bg-white dark:bg-background"
                  }
                >
                  <td className="p-1.5 font-semibold text-foreground">{c.name}<Cite n={c.cite} /></td>
                  <td className="p-1.5 text-foreground/80">{c.model}</td>
                  <td className="p-1.5 text-foreground/80">{c.role}</td>
                  <td className="p-1.5 text-foreground/80">{c.threat}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </PageContainer>
  );
}

function FeatureMatrixPage() {
  const features = [
    { name: "Social post to order link", weight: 5, eatzy: "YES", owner: "No", chownow: "No", gloria: "No", menufy: "No" },
    { name: "Cross-post to IG + FB", weight: 5, eatzy: "YES", owner: "No", chownow: "No", gloria: "No", menufy: "No" },
    { name: "Menu item linking to content", weight: 5, eatzy: "YES", owner: "No", chownow: "No", gloria: "No", menufy: "No" },
    { name: "Daily specials workflow", weight: 5, eatzy: "YES", owner: "Partial", chownow: "No", gloria: "No", menufy: "No" },
    { name: "Commission-free ordering", weight: 4, eatzy: "YES", owner: "YES", chownow: "YES", gloria: "YES", menufy: "Partial" },
    { name: "Creator / influencer ecosystem", weight: 4, eatzy: "YES", owner: "No", chownow: "No", gloria: "No", menufy: "No" },
    { name: "Post performance analytics", weight: 4, eatzy: "YES", owner: "Partial", chownow: "No", gloria: "No", menufy: "No" },
    { name: "Branded website", weight: 3, eatzy: "YES", owner: "YES", chownow: "YES", gloria: "Partial", menufy: "YES" },
    { name: "Mobile app for consumers", weight: 3, eatzy: "YES", owner: "YES", chownow: "YES", gloria: "No", menufy: "YES" },
    { name: "Loyalty / rewards program", weight: 3, eatzy: "YES", owner: "YES", chownow: "YES", gloria: "Partial", menufy: "YES" },
    { name: "Email / SMS marketing", weight: 2, eatzy: "Planned", owner: "YES", chownow: "YES", gloria: "No", menufy: "YES" },
    { name: "POS integration", weight: 2, eatzy: "Via Eatzy", owner: "YES", chownow: "YES", gloria: "YES", menufy: "YES" },
    { name: "Delivery integration", weight: 2, eatzy: "YES", owner: "YES", chownow: "Partial", gloria: "No", menufy: "No" },
    { name: "AI menu import", weight: 1, eatzy: "Planned", owner: "YES", chownow: "No", gloria: "No", menufy: "No" },
  ];

  return (
    <PageContainer>
      <h2 className="text-2xl font-bold text-blue-700 dark:text-blue-400 mb-3">
        Feature Matrix
      </h2>
      <p className="text-[10px] text-muted-foreground mb-4">
        Weight = importance to ICP buyer decision (5 = critical, 1 = nice-to-have). Sources: product pages<Cite n={[1,3,5,7,9]} />, G2/Capterra reviews<Cite n={[4,8,17]} />, internal analysis<Cite n={[13,14]} />.
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-[10px] border-collapse border border-border">
          <thead>
            <tr className="bg-blue-700 text-white">
              <th className="p-1.5 text-left font-medium">Feature</th>
              <th className="p-1 text-center font-medium w-8">Wt.</th>
              <th className="p-1 text-center font-medium">Eatzy</th>
              <th className="p-1 text-center font-medium">Owner</th>
              <th className="p-1 text-center font-medium">ChowNow</th>
              <th className="p-1 text-center font-medium">Gloria</th>
              <th className="p-1 text-center font-medium">Menufy</th>
            </tr>
          </thead>
          <tbody>
            {features.map((f, i) => (
              <tr
                key={f.name}
                className={
                  i % 2 === 0
                    ? "bg-blue-50/40 dark:bg-blue-950/20"
                    : "bg-white dark:bg-background"
                }
              >
                <td className="p-1.5 font-medium text-foreground">{f.name}</td>
                <td className="p-1 text-center">
                  <Badge
                    variant={f.weight >= 4 ? "default" : "secondary"}
                    className="text-[9px] px-1.5"
                  >
                    {f.weight}
                  </Badge>
                </td>
                <td className="p-1 text-center"><FeatureIcon value={f.eatzy} /></td>
                <td className="p-1 text-center"><FeatureIcon value={f.owner} /></td>
                <td className="p-1 text-center"><FeatureIcon value={f.chownow} /></td>
                <td className="p-1 text-center"><FeatureIcon value={f.gloria} /></td>
                <td className="p-1 text-center"><FeatureIcon value={f.menufy} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-5 p-4 bg-green-50/60 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-md">
        <h4 className="text-sm font-semibold text-green-700 dark:text-green-400 mb-1">
          Key Insight
        </h4>
        <p className="text-[10px] text-foreground/80 leading-relaxed">
          Eatzy is the ONLY platform that scores "YES" on all four weight-5
          features (social post to order, cross-posting, menu item linking, daily
          specials).<Cite n={[1,3,4,5,7,9]} /> This is the white space. Every competitor has online ordering
          — none connect social content to menu-level ordering.<Cite n={[13,14]} />
        </p>
      </div>
    </PageContainer>
  );
}

function PositioningMapPage() {
  const competitors = [
    { name: "DoorDash", x: 15, y: 15, color: "#dc2626", size: "lg" },
    { name: "Uber Eats", x: 12, y: 22, color: "#dc2626", size: "md" },
    { name: "Grubhub", x: 18, y: 18, color: "#dc2626", size: "md" },
    { name: "GloriaFood", x: 20, y: 82, color: "#ca8a04", size: "md" },
    { name: "Menufy", x: 25, y: 68, color: "#ca8a04", size: "md" },
    { name: "ChowNow", x: 32, y: 78, color: "#ca8a04", size: "md" },
    { name: "Owner.com", x: 42, y: 58, color: "#ea580c", size: "md" },
    { name: "EATZY", x: 85, y: 88, color: "#16a34a", size: "xl" },
  ];

  return (
    <PageContainer>
      <h2 className="text-2xl font-bold text-blue-700 dark:text-blue-400 mb-3">
        Positioning Map
      </h2>
      <p className="text-[10px] text-muted-foreground mb-4">
        X = Social Commerce Integration (None to Full) | Y = Cost to Restaurant (High Commission to Zero Commission). Based on pricing data<Cite n={[3,5,7,10,12,19]} /> and feature analysis<Cite n={[1,4,9,13]} />.
      </p>

      <div className="p-6 border border-border rounded-md relative" style={{ height: "560px" }}>
        <div className="absolute bottom-10 left-14 right-6 top-6 border-l-2 border-b-2 border-foreground/30">
          <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] text-muted-foreground font-semibold">
            Social Commerce Integration
          </span>
          <span className="absolute -bottom-6 left-0 text-[10px] text-muted-foreground">
            None
          </span>
          <span className="absolute -bottom-6 right-0 text-[10px] text-muted-foreground">
            Full
          </span>
          <span className="absolute top-0 -left-12 text-[10px] text-muted-foreground" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
            Zero Commission
          </span>
          <span className="absolute bottom-0 -left-12 text-[10px] text-muted-foreground" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
            High Commission
          </span>

          <div className="absolute right-2 top-2 w-[38%] h-[32%] border-2 border-dashed border-green-500/50 rounded-md bg-green-50/30 dark:bg-green-950/20 flex flex-col items-center justify-center">
            <span className="text-[10px] font-bold text-green-600 dark:text-green-400">
              WHITE SPACE
            </span>
            <span className="text-[8px] text-green-500">
              Social commerce + zero commission
            </span>
          </div>

          {competitors.map((c) => {
            const dotSize =
              c.size === "xl"
                ? "w-7 h-7"
                : c.size === "lg"
                  ? "w-5 h-5"
                  : "w-4 h-4";
            return (
              <div
                key={c.name}
                className="absolute flex flex-col items-center"
                style={{
                  left: `${c.x}%`,
                  bottom: `${c.y}%`,
                  transform: "translate(-50%, 50%)",
                }}
              >
                <div
                  className={`${dotSize} rounded-full ${c.name === "EATZY" ? "ring-2 ring-offset-2 ring-green-500" : ""}`}
                  style={{ backgroundColor: c.color }}
                />
                <span
                  className="text-[9px] font-medium mt-1 whitespace-nowrap"
                  style={{ color: c.color }}
                >
                  {c.name}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-4 p-4 bg-blue-50/60 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md">
        <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-1">
          Reading the Map
        </h4>
        <p className="text-[10px] text-foreground/80 leading-relaxed">
          Eatzy occupies the top-right quadrant: full social commerce integration
          with zero commission.<Cite n={13} /> No competitor is in this space. DoorDash, Uber Eats,
          and Grubhub are bottom-left (no social integration, high commissions at 15-30%<Cite n={[12,19]} />).
          ChowNow, GloriaFood, and Menufy cluster in the left-center (zero/low
          commissions but no social commerce<Cite n={[5,7,10]} />). Owner.com edges toward center but
          does not connect social posts to ordering<Cite n={[1,3]} />.
        </p>
      </div>
    </PageContainer>
  );
}

function WhiteSpaceKanoPage() {
  const gaps = [
    {
      title: "Social post to menu-item ordering",
      evidence: "Zero competitors connect a restaurant's Instagram/Facebook post directly to a specific menu item with one-tap ordering.",
      opportunity: "First-mover who defines this workflow owns the category.",
      cite: [1, 4, 7, 9, 13],
    },
    {
      title: "Daily specials distribution",
      evidence: "Restaurant owners post specials manually every day. No platform automates: post once, distribute everywhere, make it orderable instantly.",
      opportunity: "Fastest sales hook. Demo-to-close in under 5 minutes.",
      cite: [14, 16],
    },
    {
      title: "Creator-driven restaurant commerce",
      evidence: "Food influencers drive massive traffic but cannot convert views to orders. No platform lets creators tag menu items with order buttons.",
      opportunity: "Eatzy Kudoz consumer feed + creator rewards = a flywheel no competitor can replicate.",
      cite: [13, 14],
    },
    {
      title: "Post-level revenue attribution",
      evidence: "No platform shows \"this specific post drove $187 in orders.\" Only aggregate metrics exist.",
      opportunity: "Post-level analytics make the product addictive for retention.",
      cite: [1, 4, 13],
    },
  ];

  const kanoData = [
    { feature: "Online ordering", category: "Basic", who: "All competitors", implication: "Must have — already built", cite: [1, 4, 7, 9] },
    { feature: "Commission-free model", category: "Basic", who: "ChowNow, GloriaFood, Owner.com", implication: "Must have — already built", cite: [3, 5, 7] },
    { feature: "Branded mobile app", category: "Performance", who: "Owner.com, ChowNow, Menufy", implication: "Already built via Eatzy app", cite: [1, 5, 9] },
    { feature: "Email/SMS marketing", category: "Performance", who: "Owner.com, ChowNow", implication: "Planned — not critical for launch", cite: [1, 4] },
    { feature: "AI menu import", category: "Delighter (today)", who: "Owner.com only", implication: "High-priority for onboarding speed", cite: [1, 17] },
    { feature: "Social post to order link", category: "CATEGORY-DEFINING", who: "NOBODY", implication: "This IS the wedge", cite: [13, 14] },
    { feature: "Post-level revenue analytics", category: "Delighter", who: "NOBODY", implication: "Addiction mechanism for retention", cite: 13 },
    { feature: "Creator-driven orders", category: "Delighter", who: "NOBODY", implication: "Unique flywheel — long-term moat", cite: [13, 14] },
  ];

  return (
    <PageContainer>
      <h2 className="text-2xl font-bold text-blue-700 dark:text-blue-400 mb-5">
        White Space & Kano Analysis
      </h2>

      <div className="mb-5">
        <h3 className="text-sm font-semibold text-foreground mb-3">
          Gaps No One Serves Well
        </h3>
        <div className="space-y-2.5">
          {gaps.map((g) => (
            <div key={g.title} className="p-3 border border-blue-200 dark:border-blue-800 rounded-md">
              <h4 className="text-[11px] font-semibold text-orange-600 dark:text-orange-400 mb-1.5">
                {g.title}
              </h4>
              <p className="text-[10px] text-foreground/80 mb-0.5">
                <span className="font-medium">Evidence:</span> {g.evidence}<Cite n={g.cite} />
              </p>
              <p className="text-[10px] text-foreground/80">
                <span className="font-medium">Opportunity:</span> {g.opportunity}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-foreground mb-3">
          Kano Feature Classification
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-[10px] border-collapse border border-border">
            <thead>
              <tr className="bg-blue-700 text-white">
                <th className="p-1.5 text-left font-medium">Feature</th>
                <th className="p-1.5 text-left font-medium">Category</th>
                <th className="p-1.5 text-left font-medium">Who Has It</th>
                <th className="p-1.5 text-left font-medium">Eatzy Implication</th>
              </tr>
            </thead>
            <tbody>
              {kanoData.map((k, i) => (
                <tr
                  key={k.feature}
                  className={
                    i % 2 === 0
                      ? "bg-blue-50/40 dark:bg-blue-950/20"
                      : "bg-white dark:bg-background"
                  }
                >
                  <td className="p-1.5 font-medium text-foreground">{k.feature}</td>
                  <td className="p-1.5">
                    {k.category === "CATEGORY-DEFINING" ? (
                      <Badge className="bg-green-600 text-white text-[9px]">{k.category}</Badge>
                    ) : (
                      <span className="text-foreground/80">{k.category}</span>
                    )}
                  </td>
                  <td className="p-1.5 text-foreground/80">{k.who}<Cite n={k.cite} /></td>
                  <td className="p-1.5 text-foreground/80">{k.implication}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </PageContainer>
  );
}

function ActionPlanPage() {
  const actions = [
    {
      title: "Claim the category in all materials",
      detail: "Use \"Social Food Commerce\" or \"Social Ordering Engine\" in every pitch deck, landing page, and sales call.",
      cite: [13, 16],
    },
    {
      title: "Lead with daily specials in demos, not features",
      detail: "Demo: Open Instagram. Show a food post. Ask \"Can customers order this?\" Answer is always no. Then show Eatzy. Under 60 seconds.",
      cite: [14, 16],
    },
    {
      title: "Price at $149/mo with the $500 guarantee",
      detail: "\"If you don't redirect $500+ from DoorDash within 30 days — first month free.\" Estimated guarantee trigger rate: <5% of MRR.",
      cite: [13, 15],
    },
  ];

  const battlecard = [
    {
      scenario: "Prospect uses ChowNow",
      question: "When you post a daily special on Instagram, can your ChowNow customers order that specific item directly from the post?",
      trap: "ChowNow can't do this. The answer is always \"No.\" Then: \"That's exactly what Eatzy does.\"",
      cite: [4, 5],
    },
    {
      scenario: "Prospect uses Owner.com",
      question: "How much are you paying per month? When you post food on social media, does Owner.com let followers order that dish from the post?",
      trap: "$499/mo vs. $149/mo. And no social-to-order link. Double the pain.",
      cite: [1, 3],
    },
    {
      scenario: "\"We already have online ordering\"",
      question: "When you post tonight's special on Instagram, can followers order it with one tap?",
      trap: "No ordering system connects social posts to menu items. Eatzy doesn't replace ordering — it feeds it.",
      cite: [1, 4, 7, 9],
    },
    {
      scenario: "Prospect uses GloriaFood",
      question: "How do you let your IG followers know they can order directly from you instead of DoorDash?",
      trap: "GloriaFood has zero social distribution. Still manually posting and hoping.",
      cite: [7, 8],
    },
    {
      scenario: "\"DoorDash brings me customers\"",
      question: "How much did you pay DoorDash last month? What if you kept even 10% of those orders?",
      trap: "Convert commission pain into dollar amount. Then show QuickPost demo.",
      cite: [12, 15],
    },
  ];

  return (
    <PageContainer>
      <h2 className="text-2xl font-bold text-blue-700 dark:text-blue-400 mb-5">
        Action Plan & Sales Battlecard
      </h2>

      <div className="mb-5">
        <h3 className="text-sm font-semibold text-foreground mb-3">
          Immediate Actions
        </h3>
        <div className="space-y-2.5">
          {actions.map((a, i) => (
            <div key={i} className="p-3 bg-orange-50/40 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-md">
              <h4 className="text-[11px] font-semibold text-orange-600 dark:text-orange-400 mb-1">
                {a.title}
              </h4>
              <p className="text-[10px] text-foreground/80">{a.detail}<Cite n={a.cite} /></p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-foreground mb-2">
          Sales Battlecard: Trap-Setting Questions
        </h3>
        <p className="text-[10px] text-muted-foreground mb-3">
          Use these questions on sales calls to expose competitor weaknesses<Cite n={[13,15]} />:
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-[10px] border-collapse border border-border">
            <thead>
              <tr className="bg-blue-700 text-white">
                <th className="p-1.5 text-left font-medium w-[18%]">Scenario</th>
                <th className="p-1.5 text-left font-medium w-[40%]">Question to Ask</th>
                <th className="p-1.5 text-left font-medium w-[42%]">Why It Works</th>
              </tr>
            </thead>
            <tbody>
              {battlecard.map((b, i) => (
                <tr
                  key={i}
                  className={
                    i % 2 === 0
                      ? "bg-blue-50/40 dark:bg-blue-950/20"
                      : "bg-white dark:bg-background"
                  }
                >
                  <td className="p-1.5 font-medium text-foreground">{b.scenario}</td>
                  <td className="p-1.5 text-foreground/80 italic">{b.question}</td>
                  <td className="p-1.5 text-foreground/80">{b.trap}<Cite n={b.cite} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </PageContainer>
  );
}

function SourcesPage() {
  const sources = [
    { num: 1, text: "Owner.com Pricing & Reviews — G2 (2026)", url: "https://www.g2.com/products/owner-com/reviews" },
    { num: 2, text: "Owner.com Series C ($120M, $1B valuation) — Meritech Capital, May 2025" },
    { num: 3, text: "Owner.com Pricing Page", url: "https://www.owner.com/pricing" },
    { num: 4, text: "ChowNow Reviews & Pricing — G2 (2026)", url: "https://www.g2.com/products/chownow-for-restaurants/reviews" },
    { num: 5, text: "ChowNow Pricing — Official", url: "https://get.chownow.com/pricing/" },
    { num: 6, text: "ChowNow Funding — Crunchbase ($63.75M total)" },
    { num: 7, text: "GloriaFood Pricing & Features", url: "https://www.gloriafood.com/pricing" },
    { num: 8, text: "GloriaFood Reviews — Trustpilot (2025)" },
    { num: 9, text: "Menufy Restaurant Platform — Official", url: "https://restaurant.menufy.com" },
    { num: 10, text: "Menufy Pricing ($1.75/order) — Delivisor Review (2025)" },
    { num: 11, text: "US Restaurant Count — CHD Expert (~660K, 2023)" },
    { num: 12, text: "DoorDash Commission Rates (15-30%)", url: "https://get.doordash.com/en-us/products/marketplace" },
    { num: 13, text: "Eatzy Board-Ready Strategy & Execution Plan — Internal (March 2026)" },
    { num: 14, text: "Eatzy Social Ordering Engine Notes — Internal (March 2026)" },
    { num: 15, text: "Eatzy How To Get To 1000/Month — Internal (March 2026)" },
    { num: 16, text: "Eatzy PRIMARY NOTES: IG to Orders — Internal (March 2026)" },
    { num: 17, text: "Owner.com Capterra Reviews (2026)", url: "https://www.capterra.com/p/10002488/Owner/" },
    { num: 18, text: "Software Advice — Owner.com Profile" },
    { num: 19, text: "Grubhub Commission Rates (5-30%) — Public pricing data (2025)" },
  ];

  return (
    <PageContainer>
      <h2 className="text-2xl font-bold text-blue-700 dark:text-blue-400 mb-6">
        Sources
      </h2>
      <div className="space-y-3">
        {sources.map((s) => (
          <div key={s.num} className="flex gap-2 items-baseline">
            <span className="text-[10px] font-mono text-muted-foreground w-6 shrink-0 text-right">
              [{s.num}]
            </span>
            <div>
              <p className="text-[11px] text-foreground">{s.text}</p>
              {s.url && (
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] text-blue-600 dark:text-blue-400 hover:underline break-all"
                >
                  {s.url}
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </PageContainer>
  );
}

const PAGES = [
  CoverPage,
  ExecutiveSummaryPage,
  CompetitiveLandscapePage,
  FeatureMatrixPage,
  PositioningMapPage,
  WhiteSpaceKanoPage,
  ActionPlanPage,
  SourcesPage,
];

export default function CompetitiveReport() {
  const [currentPage, setCurrentPage] = useState(0);

  const goNext = useCallback(() => {
    setCurrentPage((p) => Math.min(p + 1, TOTAL_PAGES - 1));
  }, []);

  const goPrev = useCallback(() => {
    setCurrentPage((p) => Math.max(p - 1, 0));
  }, []);

  const PageComponent = PAGES[currentPage];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-[900px] mx-auto p-4 md:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-lg font-bold text-foreground">
              Competitive Analysis Report
            </h1>
            <p className="text-xs text-muted-foreground">
              Eatzy Social Ordering Engine — March 2026
            </p>
          </div>
          <a href="/docs/eatzy-competitive-analysis.pdf" download>
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-1.5" />
              Download PDF
            </Button>
          </a>
        </div>

        <div className="flex gap-1 mb-4 overflow-x-auto pb-1">
          {PAGE_TITLES.map((title, i) => {
            const Icon = PAGE_ICONS[i];
            return (
              <button
                key={i}
                onClick={() => setCurrentPage(i)}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
                  i === currentPage
                    ? "bg-blue-700 text-white"
                    : "text-muted-foreground hover-elevate"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{title}</span>
                <span className="sm:hidden">{i + 1}</span>
              </button>
            );
          })}
        </div>

        <div className="overflow-x-auto">
          <PageComponent />
        </div>

        <div className="flex items-center justify-between mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={goPrev}
            disabled={currentPage === 0}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Previous
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {currentPage + 1} of {TOTAL_PAGES}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={goNext}
            disabled={currentPage === TOTAL_PAGES - 1}
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
